"use client";

import React, { useEffect, useRef } from "react";
import styled from "styled-components";

// ═══════════════════════════════════════════════════════════════════════════
// SUPERPOSITION PARTICLES — v2, sprite-cached
// Same visual concept, but blur is pre-baked into a small sprite cache at
// mount instead of running ctx.filter live per-particle per-frame. Live
// canvas filters are extremely GPU-expensive (real convolution + forced
// compositing); pre-baking + drawImage() turns that into a cheap blit.
// ═══════════════════════════════════════════════════════════════════════════

interface GlyphParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  char: "0" | "1";
  sizeBucket: number; // index into SIZE_BUCKETS
  age: number;
  lifespan: number;
}

interface SuperpositionParticlesProps {
  colorRgb?: string;
  maxParticles?: number;
  spawnPerMove?: number;
  maxBlurPx?: number;
}

const CanvasEl = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const CHARS: Array<"0" | "1"> = ["0", "1"];
const SIZE_BUCKETS = [11, 14, 18]; // px — replaces continuous random size
const BLUR_STAGES = 6; // discrete decay keyframes baked into sprites

interface SpriteSet {
  canvas: HTMLCanvasElement;
  w: number;
  h: number;
}

type SpriteCache = Record<"0" | "1", SpriteSet[][]>; // [char][sizeBucket][stage]

function buildSpriteCache(
  colorRgb: string,
  maxBlurPx: number,
  dpr: number,
): SpriteCache {
  const cache = { "0": [], "1": [] } as unknown as SpriteCache;

  for (const char of CHARS) {
    cache[char] = SIZE_BUCKETS.map((size) => {
      const stages: SpriteSet[] = [];
      for (let s = 0; s < BLUR_STAGES; s++) {
        const blurPx = (s / (BLUR_STAGES - 1)) * maxBlurPx;
        const padding = Math.ceil(blurPx * 3) + 4;
        const w = size + padding * 2;
        const h = size + padding * 2;

        const off = document.createElement("canvas");
        off.width = Math.ceil(w * dpr);
        off.height = Math.ceil(h * dpr);
        const octx = off.getContext("2d");
        if (!octx) continue;

        octx.setTransform(dpr, 0, 0, dpr, 0, 0);
        // The one-time cost lives here — baked once, never repeated per frame
        octx.filter = blurPx > 0.05 ? `blur(${blurPx.toFixed(2)}px)` : "none";
        octx.font = `600 ${size}px "IBM Plex Mono", "JetBrains Mono", monospace`;
        octx.fillStyle = `rgb(${colorRgb})`;
        octx.textAlign = "center";
        octx.textBaseline = "middle";
        octx.fillText(char, w / 2, h / 2);

        stages.push({ canvas: off, w, h });
      }
      return stages;
    });
  }

  return cache;
}

export const SuperpositionParticles: React.FC<SuperpositionParticlesProps> = ({
  colorRgb = "255, 184, 92",
  maxParticles = 70,
  spawnPerMove = 2,
  maxBlurPx = 7,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<GlyphParticle[]>([]);
  const rafRef = useRef<number | null>(null);
  const runningRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    if (reducedMotion || !finePointer) return;

    let width = 0;
    let height = 0;
    // Capped at 1 (not 1.5) — sprite cache already handles its own crispness,
    // and this is the single most impactful resolution/GPU-load lever left.
    const dpr = Math.min(window.devicePixelRatio || 1, 1);

    const resize = () => {
      width = parent.clientWidth;
      height = parent.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Built once at mount — this is where the blur cost is paid, exactly once
    const spriteCache = buildSpriteCache(colorRgb, maxBlurPx, dpr);

    const tick = (now: number, lastTimeRef: { current: number }) => {
      const dt = now - lastTimeRef.current;
      lastTimeRef.current = now;

      ctx.clearRect(0, 0, width, height);

      particlesRef.current = particlesRef.current.filter((p) => {
        p.age += dt;
        if (p.age >= p.lifespan) return false;

        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);

        const progress = p.age / p.lifespan;
        const opacity = Math.max(0, 1 - progress);
        const stageIndex = Math.min(
          BLUR_STAGES - 1,
          Math.floor(progress * BLUR_STAGES),
        );
        const sprite = spriteCache[p.char][p.sizeBucket][stageIndex];
        if (!sprite) return true;

        ctx.globalAlpha = opacity;
        ctx.drawImage(
          sprite.canvas,
          p.x - sprite.w / 2,
          p.y - sprite.h / 2,
          sprite.w,
          sprite.h,
        );

        return true;
      });
      ctx.globalAlpha = 1;

      if (particlesRef.current.length > 0) {
        rafRef.current = requestAnimationFrame((t) => tick(t, lastTimeRef));
      } else {
        runningRef.current = false; // idle — loop stops until next spawn
      }
    };

    const startLoopIfIdle = () => {
      if (runningRef.current) return;
      runningRef.current = true;
      const lastTimeRef = { current: performance.now() };
      rafRef.current = requestAnimationFrame((t) => tick(t, lastTimeRef));
    };

    const handlePointerMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (x < 0 || x > width || y < 0 || y > height) return;
      if (particlesRef.current.length >= maxParticles) return;

      for (let i = 0; i < spawnPerMove; i++) {
        particlesRef.current.push({
          x: x + (Math.random() - 0.5) * 14,
          y: y + (Math.random() - 0.5) * 14,
          vx: (Math.random() - 0.5) * 0.35,
          vy: -0.25 - Math.random() * 0.45,
          char: CHARS[Math.floor(Math.random() * CHARS.length)],
          sizeBucket: Math.floor(Math.random() * SIZE_BUCKETS.length),
          age: 0,
          lifespan: 850 + Math.random() * 650,
        });
      }

      startLoopIfIdle();
    };

    window.addEventListener("pointermove", handlePointerMove as EventListener);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener(
        "pointermove",
        handlePointerMove as EventListener,
      );
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      runningRef.current = false;
    };
  }, [colorRgb, maxParticles, spawnPerMove, maxBlurPx]);

  return <CanvasEl ref={canvasRef} aria-hidden="true" />;
};

export default SuperpositionParticles;
